import React from "react";
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image";
import { Button } from "react-bootstrap";
import { stringJoin } from "../../../../../utils/functions";
import * as Styles from "./_card3d-2023.module.scss";

export default class Card3d2023Component extends React.Component<{ team: Queries.Team2023Fragment; front_only: boolean }> {
    private readonly parentCardRef = React.createRef<HTMLDivElement>();
    private readonly innerCardRef = React.createRef<HTMLSpanElement>();
    private readonly innerCardBackfaceRef = React.createRef<HTMLSpanElement>();

    private readonly glareRefs: HTMLSpanElement[] = [];

    private readonly baseDropShadowColor = "rgba(0, 0, 0, 0.3)";

    calcAngle(e: React.MouseEvent | React.TouchEvent) {
        const innerCard = this.innerCardRef.current!;
        const backfaceDiv = this.innerCardBackfaceRef.current;
        const cardDiv = this.parentCardRef.current!;

        // Get the x/y from either the MouseEvent or TouchEvent.
        let clientX, clientY;
        if ("clientX" in e) ({ clientX, clientY } = e);
        else if ("touches" in e) ({ clientX, clientY } = e.touches[0]);
        else return;

        let x = Math.abs(innerCard.getBoundingClientRect().x - clientX);
        let y = Math.abs(innerCard.getBoundingClientRect().y - clientY);

        // Calc half the width and half the height
        let halfWidth = innerCard.getBoundingClientRect().width / 2;
        let halfHeight = innerCard.getBoundingClientRect().height / 2;

        // Use this to create an angle.  Dividing by 6 & 14 found as good by trial and error.
        let calcAngleX = (x - halfWidth) / 6;
        let calcAngleY = (y - halfWidth) / 14;

        // Calculate the glare gradiant center.
        let gX = (1 - x / (halfWidth * 2)) * 100;
        let gY = (1 - y / (halfHeight * 2)) * 100;

        // Add the glare at the reflection of where the user's mouse is covering.
        const glareBg = `radial-gradient(circle at ${gX}% ${gY}%, var(--glare-color), transparent)`;
        const glareOp = "0.3";
        this.glareRefs
            .filter((el) => !!el)
            .forEach((el) => {
                el.style.setProperty("background", glareBg);
                el.style.setProperty("opacity", glareOp);
            });

        // And set its container's perspective
        const perspective = `${halfWidth * 6}px`;
        cardDiv.style.setProperty("perspective", perspective);
        innerCard.style.setProperty("perspective", perspective);

        // Reapply this to the shadow, with different divisors
        let calcShadowX = (x - halfWidth) / 3;
        let calcShadowY = (y - halfHeight) / 6;

        innerCard.style.setProperty("filter", `drop-shadow(${-calcShadowX}px ${-calcShadowY}px 15px ${this.baseDropShadowColor})`);
        innerCard.style.setProperty("transform", `rotateY(${calcAngleX}deg) rotateX(${-calcAngleY}deg) scale(1.04)`);
        if (!!backfaceDiv) backfaceDiv.style.setProperty("transform", `${innerCard.style.transform} translateZ(-8px)`);
    }

    mouseLeave() {
        this.innerCardRef.current!.style.filter = "";
        this.innerCardRef.current!.style.transform = "";
        this.innerCardRef.current!.style.perspective = "";
        if (!!this.innerCardBackfaceRef.current) this.innerCardBackfaceRef.current.style.transform = "";

        this.glareRefs.filter((el) => !!el).forEach((el) => (el.style.opacity = ""));
    }

    flipCard(e: React.MouseEvent | React.TouchEvent | null) {
        // Disable click-to-flip on devices that don't have the ability to hover.
        if (!!e && window.matchMedia("(hover: none)").matches) return;

        this.parentCardRef.current!.classList.toggle(Styles.flipped);
        this.mouseLeave();
    }

    render() {
        let teamImg = null;
        const { name, year, sport, league, city, state, colors, logo } = this.props.team?.frontmatter!;

        const borderStyle: Record<string, string> = {};
        const [color1, color2] = colors ?? [];
        if (!!color1) borderStyle["--border-color"] = color1;
        if (!!color2) borderStyle["--outline-color"] = color2;

        if (!!logo?.childImageSharp) {
            const imageData = getImage(logo.childImageSharp.gatsbyImageData);
            if (!!imageData)
                teamImg = (
                    <GatsbyImage
                        image={imageData}
                        style={borderStyle}
                        className={Styles.card3dImgContainer}
                        alt={name + " team image"}
                        imgClassName={Styles.gatsImg}
                        objectFit="contain"
                    />
                );
        }
        if (!teamImg) return null;

        return (
            <section className="d-flex d-md-block flex-column align-items-center">
                <div
                    className={stringJoin("m-4 mx-5", Styles.teamCard)}
                    title="Click to Flip!"
                    ref={this.parentCardRef}
                    onClick={(e) => this.flipCard(e)}
                    onTouchMove={(e) => this.calcAngle(e)}
                    onMouseMove={(e) => this.calcAngle(e)}
                    onMouseEnter={(e) => this.calcAngle(e)}
                    onMouseLeave={() => this.mouseLeave()}
                >
                    <span className={Styles.innerCardBackface} ref={this.innerCardBackfaceRef}>
                        <span className={Styles.flipInnerCard}>
                            <h2 className="text-center">
                                <u>{name}</u>
                            </h2>
                            <h3 className="text-center">{sport}</h3>
                            <div className="d-flex justify-content-between align-items-center">
                                <h4>
                                    <i>{league}</i>
                                </h4>
                                <h5 className="text-end ms-3">
                                    {city},<wbr />
                                    {state}
                                </h5>
                            </div>

                            <div className={Styles.logoFront}>
                                <p className="ff-serif align-self-end text-center">Official Sponsor {year}</p>
                                <StaticImage src="../../../../images/logos/pr_logo.svg" alt="Labatt Parks and Rec Program Logo" objectFit="contain" className="ms-auto" />
                            </div>
                        </span>
                        <span className={stringJoin(Styles.glare, Styles.glareBack)} ref={(el) => this.glareRefs.push(el!)}></span>
                    </span>
                    <span className={stringJoin("ff-serif", Styles.innerCard)} ref={this.innerCardRef}>
                        {teamImg}
                        <h2 className="text-center pt-3 my-auto">{name}</h2>
                        <div className={Styles.logoFront}>
                            <p className="align-self-end text-center">Official Sponsor {year}</p>
                            <StaticImage src="../../../../images/logos/pr_logo.svg" alt="Labatt Parks and Rec Program Logo" objectFit="contain" className="ms-auto" />
                        </div>
                        <span className={Styles.glare} ref={(el) => this.glareRefs.push(el!)}></span>
                    </span>
                </div>
                <Button variant="dark" className="ff-serif m-auto" onClick={() => this.flipCard(null)}>
                    Flip the Card
                </Button>
            </section>
        );
    }
}
